import React from 'react'
import styled from 'styled-components'

import { selectMaintenanceStatus } from '@hozana/api/selectors'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { useScreen } from '@hozana/screen/useScreen'
import { GTM } from '@hozana/tracking/gtm'

import { Div } from 'elements/layout/Div'
import { Li } from 'elements/layout/Li'
import { Overlay } from 'elements/layout/Overlay'
import { Ul } from 'elements/layout/Ul'

import { closeNavigation, openNavigation } from 'general/actions'
import { FULL_STATIC_PAGES, HEADER_ID, HEADER_ITEM, QUERY_ACTION } from 'general/constants'
import { useHeaderMap } from 'general/hooks/useHeaderMap'
import { useTitle } from 'general/hooks/useTitle'
import { isNavigationOpen as selectNavigationOpen } from 'general/selectors'
import { Navigation } from 'general/structure/Navigation'
import { PAGE } from 'routes/constants'

import { selectCommunity } from 'modules/community/selectors'

import { NavigationButton } from '../Navigation/NavigationButton'
import { AvoidanceNavigation } from './AvoidanceNavigation'
import { GoBack } from './GoBack'
import {
  CommunityJoinButtons,
  DonationLink,
  HeaderLinkButton,
  LanguageItemBlock,
  LoginButton,
  LogoBlock,
  SignupButton,
  TitlePage,
} from './HeaderItemsComponent'
import { HeaderWrapper } from './HeaderWrapper'

export type THeaderProps = {
  pageTitle?: string
  id?: string
}

const StyledLi = styled(Li)`
  transition: all 300ms linear;
`

export const Header: TFC<THeaderProps> = ({ pageTitle, id }) => {
  const router = useRouter()
  const screen = useScreen()
  const dispatch = useDispatch()
  const title = useTitle(pageTitle)

  const { pathname, query } = router

  const currentCommunity = useSelector((state) => selectCommunity(state, Number(query.communityId)))
  const isNavigationOpen = useSelector((state) => selectNavigationOpen(state))
  const isMaintenanceStateActive = useSelector((state) => selectMaintenanceStatus(state))

  const toggleNavigation = () => {
    const navigationWillOpen = !isNavigationOpen
    if (navigationWillOpen) {
      GTM.trackEvent(GTM.EVENTS.MENU)
      dispatch(openNavigation())
    } else {
      dispatch(closeNavigation())
    }
  }

  const renderSection = (items: HEADER_ITEM[]) => {
    // Do not display the header on SocialAuthPage
    if (router.pathname === PAGE.AUTH_SOCIAL) {
      return null
    }

    return items.map((item) => {
      switch (item) {
        case HEADER_ITEM.NAVIGATION_BUTTON:
          return !['it', 'pl'].includes(router.locale) ? (
            <NavigationButton key={item} toggleNavigation={toggleNavigation} isNavigationOpen={isNavigationOpen} />
          ) : null
        case HEADER_ITEM.TITLE_PAGE:
          return (
            <TitlePage
              key={item}
              bigTitle={!([PAGE.COMMUNITY_VIEW, PAGE.COMMUNITY_VIEW_LANDING] as PAGE[]).includes(pathname)}
            >
              {title}
            </TitlePage>
          )
        case HEADER_ITEM.LOGO:
          return <LogoBlock key={item} />
        case HEADER_ITEM.LANGUAGE_ITEM_BLOCK:
          return <LanguageItemBlock key={item} />
        case HEADER_ITEM.DONATION:
          return <DonationLink key={item} />
        case HEADER_ITEM.LOGIN_BUTTON:
          return !isMaintenanceStateActive && <LoginButton key={item} />
        case HEADER_ITEM.SIGN_UP_BUTTON:
          return !isMaintenanceStateActive && <SignupButton key={item} />
        case HEADER_ITEM.COMMUNITY_JOIN_BUTTONS:
          return <CommunityJoinButtons key={item} community={currentCommunity} />
        case HEADER_ITEM.GO_BACK:
          return <GoBack key={item} />
        case HEADER_ITEM.TO_HOZANA_LINK:
          return (
            <HeaderLinkButton to={{ pathname: PAGE.HOME }} key={item}>
              trans:common:words.discover-hozana
            </HeaderLinkButton>
          )
        case HEADER_ITEM.LOGIN_LINK:
          return (
            <HeaderLinkButton to={{ pathname: PAGE.HOME, query: { action: QUERY_ACTION.LOGIN } }} key={item}>
              trans:common:auth.login.login
            </HeaderLinkButton>
          )
        default:
          return null
      }
    })
  }

  const { desktop, mobile } = useHeaderMap({ community: currentCommunity })

  const leftItems = renderSection(screen.xs ? mobile.left : desktop.left)
  const middleItems = renderSection(screen.xs ? mobile.middle : desktop.middle)
  const rightItems = renderSection(screen.xs ? mobile.right : desktop.right)
  return (
    <>
      {isNavigationOpen && <Overlay onClick={toggleNavigation} zIndex="subMenuOverlay" />}
      {!screen.xs && <AvoidanceNavigation toggleNavigation={toggleNavigation} />}

      <HeaderWrapper
        id={id}
        bg={CONF_KEYS.API_URL.startsWith('https://hozana.org/') && CONF_KEYS.ENV === 'dev' ? 'darkRed' : undefined}
      >
        <Div as="nav" id={HEADER_ID} role="navigation" p="1em">
          <Ul color="black70" horizontal={true} align="middle">
            {screen.xs ? (
              <>
                {leftItems.map((item, index) => (
                  <Li
                    key={`mobile-left-${index}`}
                    h="100%"
                    style={index === leftItems.length - 1 ? { marginRight: 'auto' } : {}}
                  >
                    {item}
                  </Li>
                ))}
                {middleItems.map((item, index) => (
                  <Li key={`mobile-middle-${index}`} h="100%">
                    {item}
                  </Li>
                ))}
                {rightItems.map((item, index) => (
                  <Li key={`mobile-right-${index}`} h="100%" style={index === 0 ? { marginLeft: 'auto' } : {}}>
                    {item}
                  </Li>
                ))}
              </>
            ) : (
              <>
                {leftItems.map((item, index) => (
                  <Li
                    key={`desktop-left-${index}`}
                    h="100%"
                    style={index === leftItems.length - 1 ? { marginRight: 'auto' } : {}}
                  >
                    {item}
                  </Li>
                ))}
                {middleItems.map((item, index) => (
                  <Li key={`desktop-middle-${index}`} h="100%">
                    {item}
                  </Li>
                ))}
                {!['it', 'pl'].includes(router.locale) &&
                  rightItems.map((item, index) => (
                    <StyledLi key={`desktop-right-${index}`} h="100%" style={index === 0 ? { marginLeft: 'auto' } : {}}>
                      {item}
                    </StyledLi>
                  ))}
              </>
            )}
          </Ul>
        </Div>
      </HeaderWrapper>
      {!FULL_STATIC_PAGES.includes(pathname) &&
        !['it', 'pl'].includes(router.locale) &&
        [...mobile.left, ...desktop.left].includes(HEADER_ITEM.NAVIGATION_BUTTON) && (
          <Navigation toggleNavigation={toggleNavigation} isOpen={isNavigationOpen} />
        )}
    </>
  )
}
